import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  PIPELINE_STATUSES,
  type PipelineStatus,
} from "@carago/shared";
import { HotelsListView } from "../components/HotelsListView.js";
import { ApiClientError } from "../lib/api.js";
import { listHotels } from "../lib/hotels.js";

const PAGE_SIZE = 20;

function isPipelineStatus(value: string): value is PipelineStatus {
  return (PIPELINE_STATUSES as readonly string[]).includes(value);
}

export function HotelsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const pipelineRaw = searchParams.get("pipeline_status") ?? "";
  const pipelineStatus: PipelineStatus | "" = isPipelineStatus(pipelineRaw)
    ? pipelineRaw
    : "";

  const listQuery = useInfiniteQuery({
    queryKey: ["hotels", q, pipelineStatus],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      listHotels({
        q: q || undefined,
        pipeline_status: pipelineStatus || undefined,
        cursor: pageParam,
        limit: PAGE_SIZE,
      }),
    getNextPageParam: (last) => last.next_cursor,
  });

  const hotels = useMemo(
    () => listQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [listQuery.data],
  );

  const state = listQuery.isLoading
    ? "loading"
    : listQuery.isError
      ? "error"
      : hotels.length === 0
        ? "empty"
        : "ready";

  return (
    <HotelsListView
      state={state}
      errorMessage={
        listQuery.error instanceof ApiClientError
          ? listQuery.error.message
          : listQuery.error
            ? "Hotels konnten nicht geladen werden."
            : null
      }
      hotels={hotels}
      q={q}
      pipelineStatus={pipelineStatus}
      hasNextPage={Boolean(listQuery.hasNextPage)}
      loadingMore={listQuery.isFetchingNextPage}
      onQChange={(value) => {
        const next = new URLSearchParams(searchParams);
        if (value.trim()) next.set("q", value);
        else next.delete("q");
        setSearchParams(next, { replace: true });
      }}
      onPipelineStatusChange={(value) => {
        const next = new URLSearchParams(searchParams);
        if (value) next.set("pipeline_status", value);
        else next.delete("pipeline_status");
        setSearchParams(next, { replace: true });
      }}
      onLoadMore={() => {
        void listQuery.fetchNextPage();
      }}
    />
  );
}
